import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import {
  Send,
  CheckCircle2,
  AlertCircle,
  X,
  Info,
  Pencil,
  Loader2,
} from "lucide-react";
import {
  postDME,
  postImaging,
  postLab,
  postOther,
  postPatientInfo,
  postPrescription,
  postProcedure,
  postReferral,
  postVaccine,
} from "../../../api/orders";
import { useToast } from "../../../hooks/use-toast";
import {
  buildIcdOrderViewModel,
  formatIcdDisplayLines,
  getOrderIdentityKey,
} from "../../../utils/icdOrderMapping";

const getOrderLabel = (order = {}) =>
  order.selected_order_name || order.clinical_intent || order.name || order.order_type || "Order";

const stripInternalOrderFields = (order = {}) => {
  const { __persistKey, icd_code_details, icd10_code_details, ...rest } = order;
  return rest;
};

const PostIconButton = ({
  onClick,
  disabled,
  isPosted = false,
  isFailed = false,
  isPosting = false,
}) => {
  const [status, setStatus] = useState("idle");

  const handleClick = () => {
    if (status !== "idle" || disabled || isPosted || isPosting) return;

    onClick(
      () => {
        setStatus("success");
        setTimeout(() => setStatus("idle"), 3000);
      },
      () => {
        setStatus("error");
        setTimeout(() => setStatus("idle"), 3000);
      }
    );
  };

  let bgClass = "bg-white text-blue-600 border-blue-200 hover:bg-blue-50 hover:border-blue-400";
  let icon = <Send className="w-3.5 h-3.5" />;
  let label = null;

  if (isPosting) {
    bgClass = "bg-blue-50 text-blue-700 border-blue-200 w-auto px-2";
    icon = <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />;
    label = <span className="text-xs font-medium">Posting</span>;
  } else if (isPosted) {
    bgClass = "bg-emerald-50 text-emerald-700 border-emerald-200 w-auto px-2";
    icon = <CheckCircle2 className="w-3.5 h-3.5 mr-1" />;
    label = <span className="text-xs font-medium">Posted</span>;
  } else if (isFailed) {
    bgClass = "bg-rose-50 text-rose-700 border-rose-200 w-auto px-2";
    icon = <AlertCircle className="w-3.5 h-3.5 mr-1" />;
    label = <span className="text-xs font-medium">Failed</span>;
  } else if (disabled) {
    bgClass = "bg-gray-50 text-gray-400 border-gray-200 cursor-not-allowed";
  } else if (status === "success") {
    bgClass = "bg-emerald-50 text-emerald-700 border-emerald-200 w-auto px-2";
    icon = <CheckCircle2 className="w-3.5 h-3.5 mr-1" />;
    label = <span className="text-xs font-medium">Success</span>;
  } else if (status === "error") {
    bgClass = "bg-rose-50 text-rose-700 border-rose-200 w-auto px-2";
    icon = <AlertCircle className="w-3.5 h-3.5 mr-1" />;
    label = <span className="text-xs font-medium">Failed</span>;
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={disabled || status !== "idle" || isPosted || isPosting}
      className={`inline-flex items-center justify-center h-7 rounded-full border transition-all ${bgClass} ${status === "idle" ? "w-7" : ""}`}
      title={
        isPosting
          ? "Posting to Athena"
          : isPosted
          ? "Already posted"
          : isFailed
          ? "Retry post"
          : "Post to EHR"
      }
    >
      {icon}
      {label}
    </button>
  );
};

const OrdersSection = ({
  ordersData,
  billingData,
  soapNotes,
  doctorEmail,
  encounterId,
  practiceId,
  appointmentId,
  canPostToAthena = true,
}) => {
  const { toast } = useToast();

  const emptyConfirmModal = {
    open: false,
    order: null,
    onSuccess: null,
    onError: null,
  };

  const [editableOrders, setEditableOrders] = useState([]);
  const [originalOrders, setOriginalOrders] = useState([]);
  const [postingOrderKeys, setPostingOrderKeys] = useState({});
  const [editingOrderIndexes, setEditingOrderIndexes] = useState({});
  const [advancedOrderIndexes, setAdvancedOrderIndexes] = useState({});
  const [confirmModal, setConfirmModal] = useState(emptyConfirmModal);

  const renderModal = (node) => {
    if (typeof document === "undefined") return null;
    return createPortal(node, document.body);
  };

  const markOrderPosted = (order) => {
    const orderKey = order?.__persistKey;
    if (!orderKey) return;

    setEditableOrders((prev) =>
      prev.map((o) => (o.__persistKey === orderKey ? { ...o, isPosted: true } : o))
    );
  };

  const getBadgeClass = (variant) => {
    if (variant === "type") {
      return "bg-sky-50 text-sky-700 border-sky-100";
    }
    return "bg-slate-100 text-slate-700 border-slate-200";
  };

  const getPriorityBadge = (priority) => {
    const normalized = String(priority || "").toLowerCase();
    if (normalized === "stat" || normalized === "urgent") {
      return "bg-rose-50 text-rose-700 border-rose-100";
    }
    if (normalized === "routine") {
      return "bg-emerald-50 text-emerald-700 border-emerald-100";
    }
    return "bg-slate-100 text-slate-700 border-slate-200";
  };

  const getOrderStatusBadge = (isPosting, isPosted, isFailed) => {
    if (isPosting) {
      return { label: "Posting", style: "bg-blue-50 text-blue-700 border-blue-100" };
    }
    if (isPosted) {
      return { label: "Posted", style: "bg-emerald-50 text-emerald-700 border-emerald-100" };
    }
    if (isFailed) {
      return { label: "Failed", style: "bg-rose-50 text-rose-700 border-rose-100" };
    }
    return { label: "Pending", style: "bg-slate-50 text-slate-700 border-slate-200" };
  };

  useEffect(() => {
    const uniqueOrders = [];
    const seen = new Set();
    const incoming = ordersData?.orders || [];

    for (const [index, order] of incoming.entries()) {
      const key = getOrderIdentityKey(order, index).toLowerCase();

      if (!seen.has(key)) {
        seen.add(key);
        const persistedOrder = {
          ...order,
          __persistKey: getOrderIdentityKey(order, index),
        };
        uniqueOrders.push(persistedOrder);
      }
    }

    const viewModel = buildIcdOrderViewModel({
      orders: uniqueOrders,
      billingSource: billingData,
      soapNotes,
    });

    setEditableOrders(viewModel.mappedOrders.map((entry) => ({
      ...entry.order,
      __persistKey: entry.orderKey,
      icd_codes: entry.icdCodes,
      icd10_codes: entry.icdCodes,
      diagnosis_codes: entry.icdCodes,
      icd_code_details: entry.icdDetails,
      diagnosis: entry.primaryIcd?.description || entry.order.diagnosis || "",
    })));
    setOriginalOrders(viewModel.mappedOrders.map((entry) => ({
      ...entry.order,
      __persistKey: entry.orderKey,
      icd_codes: entry.icdCodes,
      icd10_codes: entry.icdCodes,
      diagnosis_codes: entry.icdCodes,
      icd_code_details: entry.icdDetails,
      diagnosis: entry.primaryIcd?.description || entry.order.diagnosis || "",
    })));
    setEditingOrderIndexes({});
    setAdvancedOrderIndexes({});
    setPostingOrderKeys({});
  }, [ordersData, billingData, soapNotes]);

  const isOrderPosted = (order) => order?.isPosted === true;
  const isOrderFailed = (order) => false;
  const isOrderPosting = (order) => Boolean(postingOrderKeys[order?.__persistKey]);

  const handleEditOrder = (index) => {
    const order = editableOrders[index];
    if (!order || isOrderPosted(order)) return;

    setEditingOrderIndexes((prev) => ({ ...prev, [index]: true }));
  };

  const handleCancelEdit = (index) => {
    const originalOrder = originalOrders[index];
    if (!originalOrder) return;

    setEditableOrders((prev) => {
      const next = [...prev];
      next[index] = originalOrder;
      return next;
    });

    setEditingOrderIndexes((prev) => {
      const next = { ...prev };
      delete next[index];
      return next;
    });

    setAdvancedOrderIndexes((prev) => {
      const next = { ...prev };
      delete next[index];
      return next;
    });
  };

  const handleDoneEdit = (index) => {
    setEditingOrderIndexes((prev) => {
      const next = { ...prev };
      delete next[index];
      return next;
    });
  };

  const handleToggleAdvancedFields = (index) => {
    setAdvancedOrderIndexes((prev) => {
      const next = { ...prev };
      if (next[index]) {
        delete next[index];
      } else {
        next[index] = true;
      }
      return next;
    });
  };

  const handleEditField = (index, field, value) => {
    setEditableOrders((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const clearOrderFailure = (index) => {
    const orderKey = editableOrders[index]?.__persistKey;
    if (!orderKey) return;
  };

  const clearOrderPosting = (order) => {
    const orderKey = order?.__persistKey;
    if (!orderKey) return;
    setPostingOrderKeys((prev) => {
      if (!prev[orderKey]) return prev;
      const next = { ...prev };
      delete next[orderKey];
      return next;
    });
  };

  const postOrder = async (order, doctorEmail, encounterId, practiceId, appointmentId) => {
    let res;
    const payload = stripInternalOrderFields(order);

    if (order.order_type === "Imaging") {
      res = await postImaging(doctorEmail, encounterId, appointmentId, payload, practiceId);
    } else if (order.order_type === "Lab") {
      res = await postLab(doctorEmail, encounterId, appointmentId, payload, practiceId);
    } else if (order.order_type === "Procedure") {
      res = await postProcedure(doctorEmail, encounterId, appointmentId, payload, practiceId);
    } else if (order.order_type === "Other") {
      res = await postOther(doctorEmail, encounterId, appointmentId, payload, practiceId);
    } else if (order.order_type === "Referral") {
      res = await postReferral(doctorEmail, encounterId, appointmentId, payload, practiceId);
    } else if (order.order_type === "Vaccine") {
      res = await postVaccine(doctorEmail, encounterId, appointmentId, payload, practiceId);
    } else if (order.order_type === "PatientInfo") {
      res = await postPatientInfo(doctorEmail, encounterId, appointmentId, payload, practiceId);
    } else if (order.order_type === "Prescription") {
      res = await postPrescription(doctorEmail, encounterId, appointmentId, payload, practiceId);
    } else if (order.order_type === "DME") {
      res = await postDME(doctorEmail, encounterId, appointmentId, payload, practiceId);
    } else {
      throw new Error(`Unsupported order type: ${order.order_type}`);
    }

    if (!res) {
      throw new Error("Order post failed");
    }

    if (res.success === false) {
      throw new Error(res.message || "Order post failed");
    }

    return res;
  };

  const showOrderFailureToast = (order, message) => {
    toast({
      title: `Failed to post ${getOrderLabel(order)}`,
      description: message || "Order post failed",
    });
  };

  const ConfirmModal = () => {
    if (!confirmModal.open) return null;
    const isConfirmPosting = isOrderPosting(confirmModal.order);

    return renderModal(
      <div className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/40 backdrop-blur-sm">
        <div className="w-full max-w-md rounded-3xl border border-slate-200 bg-white p-6 shadow-2xl">
          <button
            onClick={() => setConfirmModal(emptyConfirmModal)}
            className="absolute right-4 top-4 inline-flex h-8 w-8 items-center justify-center rounded-full text-slate-500 transition hover:bg-slate-100 hover:text-slate-900"
            aria-label="Close confirmation dialog"
          >
            <X size={18} />
          </button>

          <h3 className="text-lg font-semibold text-slate-900">Confirm Post</h3>

          <div className="mt-4 space-y-4 text-sm text-black">
            <p>
              You are about to post <span className="font-semibold text-slate-900">{confirmModal.order?.selected_order_name || confirmModal.order?.clinical_intent || confirmModal.order?.name}</span> to Athena.
            </p>
            <div className="rounded-2xl border border-rose-100 bg-rose-50 px-4 py-3 text-sm text-rose-800">
              This is a one-time post. Review the details carefully. After posting, this order cannot be edited in Seismic.
            </div>
          </div>

          <div className="mt-6 flex justify-end gap-3">
            <button
              onClick={() => setConfirmModal(emptyConfirmModal)}
              className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm text-slate-700 transition hover:bg-slate-50"
              disabled={isConfirmPosting}
            >
              Cancel
            </button>
            <button
              onClick={async () => {
                const orderIndex = editableOrders.findIndex((item) => item === confirmModal.order);
                const orderKey = confirmModal.order?.__persistKey;

                try {
                  if (orderKey) {
                    setPostingOrderKeys((prev) => ({ ...prev, [orderKey]: true }));
                  }
                  await postOrder(confirmModal.order, doctorEmail, encounterId, practiceId, appointmentId);
                  markOrderPosted(confirmModal.order);
                  if (orderIndex !== -1) {
                    clearOrderFailure(orderIndex);
                    setEditingOrderIndexes((prev) => {
                      const next = { ...prev };
                      delete next[orderIndex];
                      return next;
                    });
                    setAdvancedOrderIndexes((prev) => {
                      const next = { ...prev };
                      delete next[orderIndex];
                      return next;
                    });
                  }
                  confirmModal.onSuccess?.();
                } catch (error) {
                  if (orderIndex !== -1) {
                    setEditingOrderIndexes((prev) => {
                      const next = { ...prev };
                      delete next[orderIndex];
                      return next;
                    });
                    setAdvancedOrderIndexes((prev) => {
                      const next = { ...prev };
                      delete next[orderIndex];
                      return next;
                    });
                  }
                  confirmModal.onError?.(error);
                  showOrderFailureToast(confirmModal.order, error?.message || "Order post failed");
                } finally {
                  clearOrderPosting(confirmModal.order);
                }

                setConfirmModal(emptyConfirmModal);
              }}
              className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-blue-700"
              disabled={isConfirmPosting}
            >
              {isConfirmPosting ? "Posting..." : "Post"}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const readOnlyMessage = canPostToAthena
    ? "Review any order before its one-time Athena post."
    : "Orders are read-only in Seismic.";

  const activeViewModel = buildIcdOrderViewModel({
    orders: editableOrders,
    billingSource: billingData,
    soapNotes,
  });

  return (
    <div className="space-y-4 text-slate-900">
      <div className="flex flex-col gap-4 rounded-3xl border border-slate-200 bg-white p-5 shadow-sm sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.24em] text-black">Clinical Orders</p>
          <h3 className="mt-2 text-xl font-semibold text-slate-900">Order details and ICD associations</h3>
          <p className="mt-1 text-sm text-slate-500">{editableOrders.length} order{editableOrders.length === 1 ? "" : "s"} are ready for review.</p>
        </div>
        <div className="rounded-3xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
          <p className="font-medium text-slate-900">{ordersData?.confirmed ? "Confirmed" : "Draft"}</p>
          <p className="mt-1 text-slate-500">{readOnlyMessage}</p>
        </div>
      </div>

      <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
        <div className="flex items-start gap-3">
          <Info className="mt-0.5 h-4 w-4 shrink-0 text-slate-500" />
          <div className="space-y-1">
            <p className="font-medium text-slate-900">Use Review/Edit before posting.</p>
            <p className="text-slate-500">Each order shows linked ICD-10 codes from the billing response when available.</p>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        {editableOrders.length === 0 ? (
          <div className="rounded-3xl border border-slate-200 bg-white p-6 text-sm text-slate-500">
            No orders associated with this encounter.
          </div>
        ) : (
          <div className="space-y-4">
            {editableOrders.map((item, orderIndex) => {
                      const isEditing = Boolean(editingOrderIndexes[orderIndex]);
                      const isPosted = isOrderPosted(item);
                      const isFailed = isOrderFailed(item);
                      const isPosting = isOrderPosting(item);
                      const orderVm = activeViewModel.mappedOrders.find((entry) => entry.orderKey === item.__persistKey);
                      const codes = orderVm?.icdDetails || [];
                      const statusBadge = getOrderStatusBadge(isPosting, isPosted, isFailed);
                      const icdLines = formatIcdDisplayLines(codes);

                      const orderTitle = getOrderLabel(item);
                      const orderType = item.order_type || "Unknown";
                      const displayName = item.selected_order_name || item.clinical_intent || item.name || "-";
                      const notes = item.additional_instructions || item.notes || "No provider notes.";
                      const showAdvancedFields = Boolean(advancedOrderIndexes[orderIndex]);

                      const actionButtons = (
                        <div className="flex flex-wrap items-center gap-2">
                          {canPostToAthena && !isPosted && !isFailed && !isPosting && (
                            <button
                              type="button"
                              onClick={() => handleEditOrder(orderIndex)}
                              className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-700 transition hover:bg-slate-50"
                            >
                              <Pencil className="h-3.5 w-3.5" />
                              Review
                            </button>
                          )}
                          {canPostToAthena && (
                            <PostIconButton
                              onClick={(onSuccess, onError) => {
                                clearOrderFailure(orderIndex);
                                setConfirmModal({ open: true, order: item, onSuccess, onError });
                              }}
                              isPosted={isPosted}
                              isFailed={isFailed}
                              isPosting={isPosting}
                            />
                          )}
                        </div>
                      );

                      if (isEditing && !isPosted && !isFailed && !isPosting) {
                        return (
                          <div key={item.__persistKey || orderIndex} className="space-y-4 rounded-3xl border border-slate-200 bg-slate-50 px-4 py-5 shadow-sm">
                            <div className="grid gap-4 lg:grid-cols-2">
                              <div className="space-y-4">
                                <div>
                                  <p className="text-sm font-semibold text-slate-700">Clinical content</p>
                                  <input
                                    type="text"
                                    className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-blue-500 focus:outline-none"
                                    value={item.clinical_intent || item.name || ""}
                                    onChange={(e) =>
                                      item.clinical_intent !== undefined
                                        ? handleEditField(orderIndex, "clinical_intent", e.target.value)
                                        : handleEditField(orderIndex, "name", e.target.value)
                                    }
                                  />
                                </div>

                                <div className="grid gap-4 sm:grid-cols-2">
                                  <div>
                                    <p className="text-sm font-semibold text-slate-700">Type</p>
                                    <input
                                      type="text"
                                      className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-blue-500 focus:outline-none"
                                      value={item.order_type || ""}
                                      onChange={(e) => handleEditField(orderIndex, "order_type", e.target.value)}
                                    />
                                  </div>
                                  <div>
                                    <p className="text-sm font-semibold text-slate-700">Name</p>
                                    <input
                                      type="text"
                                      className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-blue-500 focus:outline-none"
                                      value={item.selected_order_name || ""}
                                      onChange={(e) => handleEditField(orderIndex, "selected_order_name", e.target.value)}
                                    />
                                  </div>
                                </div>

                                <div className="grid gap-4 sm:grid-cols-2">
                                  <div>
                                    <p className="text-sm font-semibold text-slate-700">Priority</p>
                                    <select
                                      className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-blue-500 focus:outline-none"
                                      value={item.priority || "Routine"}
                                      onChange={(e) => handleEditField(orderIndex, "priority", e.target.value)}
                                    >
                                      <option value="Routine">Routine</option>
                                      <option value="Stat">Stat</option>
                                      <option value="Urgent">Urgent</option>
                                    </select>
                                  </div>
                                </div>

                                <div>
                                  <p className="text-sm font-semibold text-slate-700">Provider notes</p>
                                  <textarea
                                    rows={3}
                                    className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-blue-500 focus:outline-none resize-y"
                                    value={item.additional_instructions || item.notes || ""}
                                    onChange={(e) =>
                                      handleEditField(
                                        orderIndex,
                                        item.additional_instructions !== undefined ? "additional_instructions" : "notes",
                                        e.target.value
                                      )
                                    }
                                    placeholder="Add provider notes..."
                                  />
                                </div>
                              </div>

                              <div className="space-y-4">
                                {!showAdvancedFields ? (
                                  <button
                                    type="button"
                                    onClick={() => handleToggleAdvancedFields(orderIndex)}
                                    className="text-sm font-semibold text-blue-600 transition hover:text-blue-700"
                                  >
                                    Add ICD note / Athena order ID
                                  </button>
                                ) : (
                                  <div className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm">
                                    <div className="flex items-center justify-between gap-3">
                                      <div>
                                        <p className="text-sm font-semibold text-slate-900">ICD note / Athena mapping</p>
                                        <p className="text-xs text-slate-500">Use only if Athena cannot match the order.</p>
                                      </div>
                                      <button
                                        type="button"
                                        onClick={() => handleToggleAdvancedFields(orderIndex)}
                                        className="text-xs font-semibold text-slate-500 transition hover:text-slate-700"
                                      >
                                        Hide
                                      </button>
                                    </div>

                                    <div className="mt-4 grid gap-4 sm:grid-cols-2">
                                      <div>
                                        <p className="text-sm font-semibold text-slate-700">ICD note</p>
                                        <input
                                          type="text"
                                          className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-blue-500 focus:outline-none"
                                          value={item.diagnosis || ""}
                                          onChange={(e) => handleEditField(orderIndex, "diagnosis", e.target.value)}
                                          placeholder="Enter ICD note"
                                        />
                                      </div>
                                      <div>
                                        <p className="text-sm font-semibold text-slate-700">Athena order ID</p>
                                        <input
                                          type="text"
                                          className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-blue-500 focus:outline-none"
                                          value={item.athena_order_id || item.order_id || ""}
                                          onChange={(e) => {
                                            handleEditField(orderIndex, "athena_order_id", e.target.value);
                                            handleEditField(orderIndex, "order_id", e.target.value);
                                          }}
                                          placeholder="Enter Athena order ID"
                                        />
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>

                            <div className="rounded-3xl border border-rose-100 bg-rose-50 px-4 py-3 text-sm text-rose-800">
                              <div className="flex items-start gap-2">
                                <Info className="mt-0.5 h-4 w-4 text-rose-800" />
                                <p>These changes are used for the Athena post only and are not saved back to Seismic.</p>
                              </div>
                            </div>

                            <div className="flex flex-wrap justify-end gap-3">
                              <button
                                type="button"
                                onClick={() => handleCancelEdit(orderIndex)}
                                className="rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
                              >
                                Cancel
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDoneEdit(orderIndex)}
                                className="rounded-full bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-blue-700"
                              >
                                Done
                              </button>
                            </div>
                          </div>
                        );
                      }

                      return (
                        <div key={item.__persistKey || orderIndex} className="rounded-3xl border border-slate-200 bg-white px-4 py-4 shadow-sm">
                          <div className="grid gap-4 lg:grid-cols-[1fr_auto] lg:items-start">
                            <div className="min-w-0 space-y-3">
                              <div className="flex flex-wrap items-center gap-2 text-sm">
                                <p className="max-w-[55%] truncate text-sm font-semibold text-slate-900">{orderTitle}</p>
                                <span className={`inline-flex rounded-full border px-2 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] ${getBadgeClass("type")}`}>
                                  {orderType}
                                </span>
                                {item.priority && (
                                  <span className={`inline-flex rounded-full border px-2 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] ${getPriorityBadge(item.priority)}`}>
                                    {item.priority}
                                  </span>
                                )}
                              </div>

                              <div className="grid gap-3 sm:grid-cols-2">
                                <div className="min-w-0">
                                  <div className="text-xs uppercase tracking-[0.18em] font-semibold text-black">Order name</div>
                                  <div className="mt-1 truncate text-sm text-black">{displayName}</div>
                                </div>
                                <div className="relative">
                                  <div className="text-xs uppercase tracking-[0.18em] font-semibold text-black">Status</div>
                                  <span className={`mt-1 inline-flex rounded-full border px-2 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] ${statusBadge.style}`}>
                                    {statusBadge.label}
                                  </span>
                                  <div className="mt-3 flex min-w-0 items-start gap-1 text-sm leading-6 text-slate-800">
                                    <span className="shrink-0 text-sm font-semibold text-slate-900">
                                      ICD-10:
                                    </span>
                                    <div className="min-w-0 space-y-1">
                                      {icdLines.map((line, index) => (
                                        <div key={`${item.__persistKey || orderIndex}-icd-${index}`} className="break-words">
                                          {line.replace(/^ICD-10\s*:\s*/i, "")}
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                </div>
                              </div>

                              <div className="grid gap-3 sm:grid-cols-[1fr_auto]">
                                <div className="min-w-0 text-sm leading-6 text-black max-h-20 overflow-hidden whitespace-pre-wrap">
                                  {notes}
                                </div>
                                <div className="grid gap-2 text-right text-xs text-slate-500">
                                  {(item.athena_order_id || item.order_id) && (
                                    <div>
                                      <span className="font-semibold text-slate-700">Athena ID</span>
                                      <div className="mt-1 truncate">{item.athena_order_id || item.order_id}</div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>

                            <div className="flex items-start justify-end gap-2">
                              {actionButtons}
                            </div>
                          </div>
                        </div>
                      );
            })}
          </div>
        )}
      </div>

      {canPostToAthena && <ConfirmModal />}
    </div>
  );
};

export default OrdersSection;
